import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PdfPreview from './PdfPreview';
import { withCardIcon } from '../lib/uiIcons';
import { apiAiAssist, apiAiCredits, type SetlistDTO } from '../api';

/**
 * SetlistTab — the "Setlist" tab of the Show detail page: pick the planned
 * setlist for this show, optionally copy it as the "actually played" setlist
 * (an editable show-specific copy), edit/export/preview the PDF.
 *
 * Extracted from ShowDetailPage (2026-08-14, Vague 3 pass 6). Props-driven.
 *
 * Roadcase (P4c): chrome harmonised to the `.rc-*` language — `.rc-card-head`
 * + mono uppercase `.rc-header-title` (+ `.rc-header-sub`) with the AI prepare
 * action in `.rc-header-actions`, the two pickers in one `.rc-form-row`, the
 * played setlist in a `.rc-card col` panel whose song rows are `.rc-list`
 * items: `.rc-form-row` rows while editing (they wrap instead of forcing a
 * 600px line at 390) and `.rc-card` rows when reading. REORDER: this component
 * has NO drag/drop system — the played list is plain array order (`order: i`
 * is written on save), so the managed-list patron is safe here; the row
 * mechanics (add / delete / separator toggle / save) are untouched.
 * ZERO logic touched.
 */
export interface SetlistTabProps {
  setlistId: number | null;
  playedSetlistId: number | null;
  setlists: SetlistDTO[];
  assigning: boolean;
  assignSetlist: (id: number | null) => void;
  assignPlayed: (id: number | null) => void;
  copyingPlayed: boolean;
  copyPlannedAsPlayed: () => void;
  editingPlayed: boolean;
  setEditingPlayed: (b: boolean) => void;
  playedSongs: any[];
  setPlayedSongs: (s: any[]) => void;
  savePlayedSongs: () => void;
  savingPlayed: boolean;
  loadPlayedSongs: (pid: number | null) => void;
  exportSetlistPdf: () => Promise<string | null>;
  exportingSetlist: boolean;
  setlistPdfUrl: string | null;
  setlistPdfDoc: any;
  exportShowIcs: () => void;
  bandId: string | undefined;
  /** The show id (used as the prepare_setlist target so the proposal adapts to its duration). */
  showId?: string | number | null;
}

export default function SetlistTab(props: SetlistTabProps) {
  const { t } = useTranslation();
  const {
    setlistId, playedSetlistId, setlists, assigning, assignSetlist, assignPlayed,
    copyingPlayed, copyPlannedAsPlayed, editingPlayed, setEditingPlayed,
    playedSongs, setPlayedSongs, savePlayedSongs, savingPlayed, loadPlayedSongs,
    exportSetlistPdf, exportingSetlist, setlistPdfUrl, setlistPdfDoc, exportShowIcs, bandId,
    showId,
  } = props;

  // ---- AI copilot: prepare setlist (read-only proposal) ----
  const [preparing, setPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<{
    proposal?: { song?: string; artist?: string; duration?: string; note?: string }[];
    rationale?: string;
    estimatedDuration?: string;
  } | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  useEffect(() => {
    apiAiCredits().then((d) => { setCredits(d.credits); setUnlimited(!!d.unlimited); }).catch(() => setCredits(null));
  }, []);
  const outOfCredits = !unlimited && credits !== null && credits <= 0;

  const prepareSetlist = useCallback(async () => {
    if (!bandId || preparing) return;
    setPreparing(true);
    setPrepareError(null);
    setProposal(null);
    try {
      const result = await apiAiAssist({
        task: 'prepare_setlist',
        bandId,
        ...(showId != null && showId !== '' ? { showId } : {}),
      });
      setProposal((result.proposedFields ?? {}) as any);
    } catch (e: any) {
      setPrepareError(e?.message || t('prepareSetlist.failed'));
    } finally {
      setPreparing(false);
    }
  }, [bandId, preparing, showId]);

  return (
    <div className="card" data-setlist-tab style={{ maxWidth: 1000, marginBottom: 20 }}>
      <div className="rc-card-head">
        <div>
          <h3 className="rc-header-title">{withCardIcon('show-setlist:setlist', t('setlistTab.title'))}</h3>
          <p className="rc-header-sub">{t('setlistTab.subtitle')}</p>
        </div>
        {bandId && (
          <div className="rc-header-actions">
            <button className="btn btn-secondary" onClick={() => void prepareSetlist()} disabled={preparing || outOfCredits}>
              {preparing ? t('prepareSetlist.running') : outOfCredits ? t('prepareSetlist.outOfCredits') : t('prepareSetlist.button')}
            </button>
            {credits !== null && <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>{unlimited ? t('prepareSetlist.balanceUnlimited') : t('prepareSetlist.balance', { count: credits })}</span>}
          </div>
        )}
      </div>

      {/* AI copilot — prepare setlist (read-only proposal) */}
      {bandId && (preparing || prepareError || proposal) && (
        <div className="ai-import-review" data-setlist="ai" style={{ marginTop: 4, marginBottom: 12 }}>
          {preparing && <p style={{ color: 'var(--text-dim)' }}>{t('prepareSetlist.running')}</p>}
          {!preparing && prepareError && (
            <div className="ai-import-error" role="alert">{prepareError}</div>
          )}
          {!preparing && proposal && (
            <>
              <p className="ai-import-banner" style={{ fontSize: 'var(--font-sm)' }}>
                {t('prepareSetlist.readOnly')}
              </p>
              {proposal.estimatedDuration ? (
                <p style={{ margin: '4px 0 8px', fontSize: 'var(--font-sm)' }}>
                  <strong>{t('prepareSetlist.estimatedDuration')}:</strong> {proposal.estimatedDuration}
                </p>
              ) : null}
              {proposal.rationale ? (
                <p style={{ margin: '4px 0 8px', fontSize: 'var(--font-sm)' }}>
                  <strong>{t('prepareSetlist.rationale')}:</strong> {proposal.rationale}
                </p>
              ) : null}
              {!proposal.proposal || proposal.proposal.length === 0 ? (
                <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: 0 }}>{t('prepareSetlist.noProposal')}</p>
              ) : (
                <>
                  <h4 className="rc-section-title" style={{ margin: '8px 0 6px' }}>{t('prepareSetlist.proposalTitle')}</h4>
                  <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {proposal.proposal.map((s, i) => (
                      <li key={i} style={{ fontSize: 'var(--font-sm)' }}>
                        <strong>{s.song || '—'}</strong>
                        {s.artist ? ` — ${s.artist}` : ''}
                        {s.duration ? ` (${s.duration})` : ''}
                        {s.note ? <span style={{ color: 'var(--text-dim)' }}> — {s.note}</span> : null}
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </>
          )}
        </div>
      )}
      {/* Which setlist is planned / which one was actually played. */}
      <div className="rc-form-row" data-setlist="assign" style={{ alignItems: 'flex-start' }}>
        <div className="form-field" style={{ flex: '1 1 260px', minWidth: 0 }}>
          <label>{t('setlistTab.setlistForShow')}</label>
          <select
            className="form-input"
            value={setlistId != null ? String(setlistId) : ''}
            disabled={assigning || setlists.length === 0}
            onChange={(e) => assignSetlist(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t('setlistTab.noSetlist')}</option>
            {setlists.map((sl) => (
              <option key={sl.id} value={sl.id}>
                {sl.name} ({t('setlistTab.songs', { count: (sl.songs || []).length })})
              </option>
            ))}
          </select>
        </div>
        <div className="form-field" style={{ flex: '1 1 260px', minWidth: 0 }}>
          <label>{t('setlistTab.playedSetlist')}</label>
          {playedSetlistId != null ? (
            <select
              className="form-input"
              value={String(playedSetlistId)}
              disabled={assigning || setlists.length === 0}
              onChange={(e) => assignPlayed(e.target.value ? Number(e.target.value) : null)}
            >
              {setlists.map((sl) => (
                <option key={sl.id} value={sl.id}>
                  {sl.name} ({t('setlistTab.songs', { count: (sl.songs || []).length })})
                </option>
              ))}
            </select>
          ) : (
            <p style={{ color: 'var(--text-dim)', margin: '0 0 8px' }}>
              {t('setlistTab.noPlayedYet')}
            </p>
          )}
          <button
            className="btn btn-small btn-secondary"
            style={{ marginTop: 4 }}
            disabled={copyingPlayed || setlistId == null}
            onClick={copyPlannedAsPlayed}
          >
            {copyingPlayed ? t('setlistTab.copying') : t('setlistTab.copyPlanned')}
          </button>
        </div>
      </div>

      {/* The "actually played" copy: read it, or edit its songs. */}
      {playedSetlistId != null && (
        <div data-setlist="played" style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div className="rc-card-head" style={{ marginBottom: 0 }}>
            <div>
              <h4 className="rc-section-title" style={{ margin: 0 }}>{t('setlistTab.editPlayed')}</h4>
            </div>
            <div className="rc-header-actions">
              {!editingPlayed ? (
                <button className="btn btn-small btn-secondary" onClick={() => { loadPlayedSongs(playedSetlistId); setEditingPlayed(true); }}>
                  {t('setlistTab.edit')}
                </button>
              ) : (
                <>
                  <button className="btn btn-small btn-primary" disabled={savingPlayed} onClick={savePlayedSongs}>
                    {savingPlayed ? t('setlistTab.saving') : t('setlistTab.save')}
                  </button>
                  <button className="btn btn-small btn-secondary" onClick={() => { setEditingPlayed(false); loadPlayedSongs(playedSetlistId); }}>
                    {t('setlistTab.cancel')}
                  </button>
                </>
              )}
            </div>
          </div>
          {editingPlayed ? (
            <>
              <div className="rc-list" data-setlist="songs-edit">
                {playedSongs.map((s, i) => (
                  <div key={i} className="rc-form-row" data-setlist-song>
                    <span className="rc-chip">{s.is_separator ? '—' : i + 1}</span>
                    <input
                      className="form-input"
                      style={{ flex: '2 1 160px' }}
                      placeholder={t('setlistTab.titlePh')}
                      value={s.title || ''}
                      disabled={s.is_separator}
                      onChange={(e) => { const n = [...playedSongs]; n[i] = { ...n[i], title: e.target.value }; setPlayedSongs(n); }}
                    />
                    <input
                      className="form-input"
                      style={{ flex: '1 1 130px' }}
                      placeholder={t('setlistTab.remarksPh')}
                      value={s.remarks || ''}
                      disabled={s.is_separator}
                      onChange={(e) => { const n = [...playedSongs]; n[i] = { ...n[i], remarks: e.target.value }; setPlayedSongs(n); }}
                    />
                    <input
                      className="form-input"
                      style={{ flex: '0 1 90px' }}
                      placeholder={t('setlistTab.durationPh')}
                      value={s.duration || ''}
                      disabled={s.is_separator}
                      onChange={(e) => { const n = [...playedSongs]; n[i] = { ...n[i], duration: e.target.value }; setPlayedSongs(n); }}
                    />
                    <input
                      className="form-input"
                      style={{ flex: '0 1 70px' }}
                      placeholder={t('setlistTab.tempoPh')}
                      value={s.tempo || ''}
                      disabled={s.is_separator}
                      onChange={(e) => { const n = [...playedSongs]; n[i] = { ...n[i], tempo: e.target.value }; setPlayedSongs(n); }}
                    />
                    <input
                      className="form-input"
                      style={{ flex: '0 1 70px' }}
                      placeholder={t('setlistTab.keyPh')}
                      value={s.keys || ''}
                      disabled={s.is_separator}
                      onChange={(e) => { const n = [...playedSongs]; n[i] = { ...n[i], keys: e.target.value }; setPlayedSongs(n); }}
                    />
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => {
                        const n = [...playedSongs];
                        const sep = !n[i].is_separator;
                        n[i] = { ...n[i], is_separator: sep, title: sep ? (n[i].title || '— Separator —') : '' };
                        setPlayedSongs(n);
                      }}
                    >
                      {s.is_separator ? t('setlistTab.song') : t('setlistTab.sep')}
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => { const n = playedSongs.filter((_, j) => j !== i); setPlayedSongs(n); }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-small btn-secondary"
                style={{ marginTop: 10 }}
                onClick={() => { setPlayedSongs([...playedSongs, { title: '', remarks: '', duration: '', is_separator: false, order: playedSongs.length }]); }}
              >
                {t('setlistTab.addSong')}
              </button>
            </>
          ) : (
            <div className="rc-list" data-setlist="songs">
              {playedSongs.map((s, i) => (
                <div key={i} className="rc-card" data-setlist-song style={s.is_separator ? { fontStyle: 'italic' } : undefined}>
                  <span className="rc-chip">{s.is_separator ? '—' : i + 1}</span>
                  <span className="rc-card-body rc-card-name" style={{ fontWeight: s.is_separator ? 400 : 600 }}>
                    {s.is_separator ? (s.title || '— Separator —') : `${s.title || '—'}${s.remarks ? ` — ${s.remarks}` : ''}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {setlists.length === 0 && (
        <p style={{ color: 'var(--text-dim)' }}>
          {t('setlistTab.noSetlists')}
        </p>
      )}

      {setlistId != null && (
        <div className="rc-card-fields" data-setlist="exports" style={{ marginTop: 12 }}>
          <button className="btn btn-primary" onClick={() => exportSetlistPdf()} disabled={exportingSetlist}>
            {exportingSetlist ? t('setlistTab.generating') : t('setlistTab.exportPdf')}
          </button>
          <button
            className="btn btn-secondary"
            disabled={exportingSetlist}
            onClick={async () => {
              const url = setlistPdfUrl || (await exportSetlistPdf());
              if (!url) return;
              const a = document.createElement('a');
              a.href = url;
              a.download = 'setlist.pdf';
              document.body.appendChild(a);
              a.click();
              a.remove();
            }}
          >
            {t('setlistTab.open')}
          </button>
          <button
            className="btn btn-secondary"
            disabled={exportingSetlist}
            onClick={async () => {
              const url = setlistPdfUrl || (await exportSetlistPdf());
              if (!url) return;
              const iframe = document.createElement('iframe');
              iframe.style.position = 'fixed';
              iframe.style.right = '0';
              iframe.style.bottom = '0';
              iframe.style.width = '0';
              iframe.style.height = '0';
              iframe.style.border = '0';
              iframe.src = url;
              iframe.onload = () => {
                try { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); } catch { /* manual */ }
                setTimeout(() => iframe.remove(), 1000);
              };
              document.body.appendChild(iframe);
            }}
          >
            {t('setlistTab.print')}
          </button>
          <button
            className="btn btn-secondary"
            disabled={exportingSetlist}
            onClick={exportShowIcs}
          >
            {t('setlistTab.exportIcs')}
          </button>
        </div>
      )}
      {setlistPdfUrl && (
        <div data-setlist="preview" style={{ marginTop: 12 }}>
          {setlistPdfDoc && <PdfPreview docDef={setlistPdfDoc} height={480} />}
        </div>
      )}
    </div>
  );
}
